/**
 * Build per-slot review lists for City Planning — mirrors CityPlanningView
 * organizePlanByDaysFromAggregated + SlotSuggestionInterleaver + catalog fallback.
 */

import type { ViatorProduct } from '../services/viatorProductsService';
import type {
  AggregatedCatalogActivity,
  AggregatedCatalogMeal,
  AggregatedPlanItem,
  PlanTimeSlot,
} from './aggregatedPlanData';
import { normalizePlanTitle } from './aggregatedPlanData';
import { ORDERED_DAILY_SLOTS } from './cityPlanningSlots';
import { defaultTimesForSlot } from './planItemSave';
import {
  buildSlotSuggestionInterleave,
  filterViatorForSlot,
  isMealSlot,
  type SlotSuggestionEntry,
} from './slotSuggestionInterleaver';
import {
  fromStoredSightseeingStyle,
  type SightseeingStyle,
} from './sightseeingStyle';

export type { SlotSuggestionEntry };
export type SlotReviewItem = SlotSuggestionEntry & {
  /** Meal items already on the trip — shown with Scheduled badge. */
  isScheduled?: boolean;
};

const MEAL_SLOTS: PlanTimeSlot[] = ['breakfast', 'lunch', 'dinner'];
const ACTIVITY_SLOTS: PlanTimeSlot[] = ['morning', 'afternoon', 'evening'];

export function preferredSlotsForProduct(product: ViatorProduct): PlanTimeSlot[] {
  const meal = product.isMeal === true;
  return meal ? MEAL_SLOTS : ACTIVITY_SLOTS;
}

function catalogItemAsPlan(
  raw: AggregatedCatalogActivity | AggregatedCatalogMeal,
  slot: PlanTimeSlot,
  itemType: string
): AggregatedPlanItem {
  const defaults = defaultTimesForSlot(slot);
  return {
    id: raw.id || `catalog-${normalizePlanTitle(raw.title)}-${slot}`,
    title: raw.title,
    normalizedTitle: raw.normalizedTitle || normalizePlanTitle(raw.title),
    description: raw.description,
    matchScore: raw.matchScore,
    itemType,
    timeSlot: slot,
    startTime: defaults.startTime,
    endTime: defaults.endTime,
    location: raw.area,
    area: raw.area,
    url: raw.url,
    tags: raw.tags ?? [],
    personaCount: 1,
    allTags: raw.tags ?? [],
    allReasonings: [],
    isOption: false,
    cuisine: 'cuisine' in raw ? raw.cuisine : null,
  };
}

/**
 * Catalog fallback when a slot has no plan items — up to 5 tag-matched entries
 * (excludes viator tag). Mirrors iOS getCatalogSuggestionsFor.
 */
export function catalogSuggestionsForSlot(options: {
  slot: PlanTimeSlot;
  activityCatalog?: AggregatedCatalogActivity[] | null;
  mealCatalog?: AggregatedCatalogMeal[] | null;
  selectedNormalizedTitles: Set<string>;
}): AggregatedPlanItem[] {
  const slotTag = options.slot.toLowerCase();
  const tagMatch = (tags: string[]): boolean => {
    if (tags.some((t) => t.toLowerCase() === 'viator')) return false;
    if (tags.length === 0) return true;
    return tags.some((tag) => {
      const t = tag.toLowerCase();
      return t.includes(slotTag) || slotTag.includes(t);
    });
  };

  if (isMealSlot(options.slot)) {
    const meals = (options.mealCatalog ?? []).filter((m) => tagMatch(m.tags ?? []));
    return meals
      .filter((m) => {
        const key = normalizePlanTitle(m.normalizedTitle || m.title);
        return !options.selectedNormalizedTitles.has(key);
      })
      .slice(0, 5)
      .map((m) => catalogItemAsPlan(m, options.slot, 'meal'));
  }

  const activities = (options.activityCatalog ?? []).filter((a) =>
    tagMatch(a.tags ?? [])
  );
  return activities
    .filter((a) => {
      const key = normalizePlanTitle(a.normalizedTitle || a.title);
      return !options.selectedNormalizedTitles.has(key);
    })
    .slice(0, 5)
    .map((a) => catalogItemAsPlan(a, options.slot, 'activity'));
}

function partitionMealItems(
  items: AggregatedPlanItem[],
  selected: Set<string>
): { available: AggregatedPlanItem[]; booked: AggregatedPlanItem[] } {
  const available: AggregatedPlanItem[] = [];
  const booked: AggregatedPlanItem[] = [];
  for (const item of items) {
    const key = normalizePlanTitle(item.normalizedTitle || item.title);
    if (selected.has(key)) {
      booked.push(item);
    } else {
      available.push(item);
    }
  }
  return { available, booked };
}

/**
 * Build per-slot review lists: AI/catalog items interleaved with Viator by
 * sightseeing style; meals keep selected items at the end with Scheduled badge.
 */
export function buildSlotReviewItems(options: {
  slotOptions: Record<string, AggregatedPlanItem[]>;
  visibleSlots: PlanTimeSlot[];
  products: ViatorProduct[];
  selectedNormalizedTitles: Set<string>;
  sightseeingStyle?: SightseeingStyle | string | null;
  activityCatalog?: AggregatedCatalogActivity[] | null;
  mealCatalog?: AggregatedCatalogMeal[] | null;
}): Record<PlanTimeSlot, SlotReviewItem[]> {
  const style = fromStoredSightseeingStyle(
    typeof options.sightseeingStyle === 'string'
      ? options.sightseeingStyle
      : options.sightseeingStyle ?? null
  );
  const out = {} as Record<PlanTimeSlot, SlotReviewItem[]>;
  for (const slot of ORDERED_DAILY_SLOTS) {
    out[slot] = [];
  }

  const usedTourCodes = new Set<string>();

  for (const slot of options.visibleSlots) {
    const rawPlan = options.slotOptions[slot] ?? [];
    let catalog: AggregatedPlanItem[] = [];
    let bookedMeals: AggregatedPlanItem[] = [];

    if (isMealSlot(slot)) {
      const { available, booked } = partitionMealItems(
        rawPlan,
        options.selectedNormalizedTitles
      );
      catalog = available;
      bookedMeals = booked;
    } else {
      catalog = rawPlan.filter((item) => {
        const key = normalizePlanTitle(item.normalizedTitle || item.title);
        return !options.selectedNormalizedTitles.has(key);
      });
    }

    if (catalog.length === 0) {
      catalog = catalogSuggestionsForSlot({
        slot,
        activityCatalog: options.activityCatalog,
        mealCatalog: options.mealCatalog,
        selectedNormalizedTitles: options.selectedNormalizedTitles,
      });
    }

    const tours = filterViatorForSlot(options.products, slot).filter((p) => {
      const key = normalizePlanTitle(p.title);
      if (options.selectedNormalizedTitles.has(key)) return false;
      if (usedTourCodes.has(p.productCode)) return false;
      return true;
    });

    // Interleaver needs catalog anchors; when the slot has only tours, show them.
    if (catalog.length === 0 && tours.length > 0) {
      for (const product of tours) {
        out[slot].push({ kind: 'viator', product, timeSlot: slot });
        usedTourCodes.add(product.productCode);
      }
      for (const meal of bookedMeals) {
        out[slot].push({ kind: 'plan', item: meal, isScheduled: true });
      }
      continue;
    }

    const interleaved = buildSlotSuggestionInterleave({
      catalog,
      tours,
      sightseeingStyle: style,
      timeSlot: slot,
    });

    for (const entry of interleaved) {
      if (entry.kind === 'viator') {
        usedTourCodes.add(entry.product.productCode);
      }
      out[slot].push(entry);
    }

    for (const meal of bookedMeals) {
      out[slot].push({ kind: 'plan', item: meal, isScheduled: true });
    }
  }

  return out;
}
